import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, BellRing } from 'lucide-react';
import { useInboxQuery, useMarkInboxReadMutation, type InboxItem } from './api';
import { usePushNotifications, type PushStatus } from './usePushNotifications';

/** How often the bell checks for something new while the app is open. */
const POLL_MS = 60_000;

/**
 * The header bell. A dot when something was sent since the member last looked,
 * and a short list of recent notifications in the same words a push would use,
 * so a member who never allowed push still hears that a letter arrived. The
 * dot shows for everyone: push is per device, so a member with it on their
 * phone can still have missed something on this screen.
 *
 * The push toggle lives at the foot of the list. Turning it on is still the
 * user gesture the permission prompt needs; nothing is requested on load.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  // What the list showed when it opened, so this visit's new items keep their
  // highlight even though opening marks them read on the server.
  const [shown, setShown] = useState<InboxItem[] | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const { data, refetch } = useInboxQuery(undefined, {
    pollingInterval: open ? 0 : POLL_MS,
    refetchOnFocus: true,
  });
  const [markRead] = useMarkInboxReadMutation();
  const push = usePushNotifications();

  const unread = data?.unread ?? 0;

  const close = useCallback(() => {
    setOpen(false);
    setShown(null);
    void refetch();
  }, [refetch]);

  const toggleOpen = useCallback(() => {
    if (open) {
      close();
      return;
    }
    setShown(data?.items ?? []);
    setOpen(true);
    if (unread > 0) void markRead().unwrap().catch(() => undefined);
  }, [open, close, data, unread, markRead]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open, close]);

  const go = (item: InboxItem) => {
    close();
    window.location.hash = item.url.split('#')[1] ?? '';
  };

  const items = shown ?? data?.items ?? [];
  const label = unread > 0 ? `Notifications, ${unread} new` : 'Notifications';

  return (
    <div className="conv-menu bell" ref={ref}>
      <button
        type="button"
        className="icon-btn bell__btn"
        onClick={toggleOpen}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        title={label}
      >
        {push.status === 'subscribed' ? <BellRing size={16} /> : <Bell size={16} />}
        {unread > 0 && !open && <span className="bell__dot" aria-hidden="true" />}
      </button>
      {open && (
        <div className="conv-menu__pop bell__pop" role="dialog" aria-label="Notifications">
          <p className="bell__title">Notifications</p>
          {items.length === 0 ? (
            <p className="bell__empty">Nothing yet. When a letter arrives for you, it will show up here.</p>
          ) : (
            <ul className="bell__list">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`bell__item${item.read ? '' : ' bell__item--new'}`}
                    onClick={() => go(item)}
                  >
                    <span className="bell__item-title">{item.title}</span>
                    <span className="bell__item-body">{item.body}</span>
                    <span className="bell__item-when">{when(item.at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <PushToggle status={push.status} busy={push.busy} onToggle={() => void push.toggle()} />
        </div>
      )}
    </div>
  );
}

/** The foot of the list: whether this device gets these as notifications too. */
function PushToggle({ status, busy, onToggle }: { status: PushStatus; busy: boolean; onToggle: () => void }) {
  if (status === 'disabled' || status === 'unsupported') return null;
  if (status === 'denied') {
    return <p className="bell__foot">Notifications are blocked for toiyp in this browser's settings.</p>;
  }
  return (
    <div className="bell__foot">
      <span>{status === 'subscribed' ? 'This device gets these as notifications.' : 'Get these on this device too?'}</span>
      <button type="button" className="linkish" onClick={onToggle} disabled={busy}>
        {status === 'subscribed' ? 'Turn off' : 'Turn on'}
      </button>
    </div>
  );
}

function when(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
